import type {
  ButtonHTMLAttributes,
  ChangeEvent,
  HTMLAttributes,
  MouseEvent,
  ReactNode,
  SelectHTMLAttributes,
} from 'react';

import React, { useRef } from 'react';
import { useTranslation } from 'react-i18next';

import { CloseIcon, PlusIcon } from '../../icons';
import { classNames } from '../../utils/class-names';
import { Tooltip } from '../tooltip';

import styles from './tabs.module.css';

export type TabProps = {
  id?: string;
  label?: ReactNode;
} & ButtonHTMLAttributes<HTMLButtonElement>;

/**
 * @param {string} [id] ID of the particular tab. The ID is required for Tabs that have `dynamic` set to true.
 * @default undefined
 * @param {ReactNode} [label] Label to be displayed in a tab.
 * @default undefined
 * @param {ReactNode} [children] Tab's content displayed in the main section of the component.
 * @default undefined
 */
export function Tab(props: TabProps) {
  return <InternalTab {...props} />;
}

export type InternalTabProps = {
  selected?: boolean;
  label?: ReactNode;
  onClose?: () => void;
} & ButtonHTMLAttributes<HTMLButtonElement>;

/**
 * This component is used internally by the Tabs component.
 * @param {boolean} [selected] Whether the tab is selected.
 * @default undefined
 * @param {ReactNode} [label] Label to be displayed in a tab.
 * @default undefined
 * @param {Function} [onClose] Callback to be executed when the close button is clicked.
 * @default undefined
 */
export function InternalTab({ selected, label, onClick, onClose, ...props }: InternalTabProps) {
  const { t } = useTranslation();
  const ref = useRef<HTMLButtonElement>(null);

  const chooseTab = (event: MouseEvent<HTMLButtonElement>) => {
    event.target === ref.current && onClick?.(event);
  };

  return (
    <button
      ref={ref}
      role="tab"
      type="button"
      onClick={chooseTab}
      aria-selected={selected}
      className={classNames(styles.tab, styles.button, selected && styles.selected)}
      {...props}
    >
      {label}

      {onClose && (
        <CloseIcon role="button" aria-label={t('tabs.button.closeTab.label')} tabIndex={0} onClick={onClose} />
      )}
    </button>
  );
}

export interface NewTabOption {
  value: string;
  disabled?: boolean;
  label: ReactNode;
}

export type NewTabProps = {
  label?: string;
  options?: NewTabOption[];
  onOptionSelect?: (selectedOption: NewTabOption) => void;
} & HTMLAttributes<HTMLDivElement>;

/**
 * This component is used internally by the Tabs component.
 * @param {boolean} [label] Label to be displayed in a tab.
 * @default undefined
 * @param {Option[]} [options] Options to be displayed in the dropdown.
 * @default undefined
 * @param {Function} onOptionSelect Callback to be executed when the dropdown value changes.
 * @default undefined
 */
export function NewTab({ label, options, onOptionSelect, ...props }: NewTabProps) {
  return (
    <div role="button" tabIndex={0} className={classNames(styles.tab, styles.button)} {...props}>
      {label}
      <PlusIcon />
      <Dropdown label={label} options={options} onOptionSelect={onOptionSelect} />
    </div>
  );
}

/**
 * This component is used internally by the Tabs component.
 * @param {boolean} [label] Label to be displayed in a tab.
 * @default ''
 * @param {Option[]} [options] Options to be displayed in the dropdown.
 * @default undefined
 * @param {Function} onOptionSelect Callback to be executed when the dropdown value changes.
 * @default undefined
 */
export function NewTabButton({ label = '', options, onOptionSelect }: NewTabProps) {
  return (
    <Tooltip label={label}>
      <button data-testid="new-tab-button" type="button" aria-label={label} className={styles.button}>
        <PlusIcon />
        <Dropdown label={label} options={options} onOptionSelect={onOptionSelect} />
      </button>
    </Tooltip>
  );
}

/**
 * This component is used internally by NewTab and NewTabButton components.
 * @param {boolean} [label] Label to be displayed in a tab.
 * @default undefined
 * @param {Option[]} [options] Options to be displayed in the dropdown.
 * @default undefined
 * @param {Function} onChange Callback to be executed when the dropdown value changes.
 * @default undefined
 */
function Dropdown({ label, options, onOptionSelect, ...props }: NewTabProps & SelectHTMLAttributes<HTMLSelectElement>) {
  const handleChange = (event: ChangeEvent<HTMLSelectElement>) => {
    const selectedOption = options?.find((option) => option.value === event.target.value);

    selectedOption && onOptionSelect?.(selectedOption);
  };

  return (
    <select className={styles.select} value={label} onChange={handleChange} data-testid="new-tab-select" {...props}>
      <option disabled>{label}</option>

      {options?.map((option) => (
        <option key={option.value} value={option.value} disabled={option.disabled}>
          {option.label}
        </option>
      ))}
    </select>
  );
}
